import { Card, LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { Section } from "components/disw";
import ErrorBox from "components/error/ErrorBox";
import DataGridView from "components/grid/DataGridView";
import CustomStore from "devextreme/data/custom_store";
import { FilterOperator } from "model/schema/globalTypes";
import { useMemo } from "react";
import { useRateAgreementWithRates } from "./hooks/useRateAgreementWithRates";

function filterOperatorSymbol(operator: FilterOperator): string {
  switch (operator) {
    case FilterOperator.EQ:
      return "==";
    case FilterOperator.LTE:
      return "\u2264";
    case FilterOperator.GTE:
      return "\u2265";
  }
}

function filterColumnCaption(column: string, operator: FilterOperator): string {
  return `${column} (${filterOperatorSymbol(operator)})`;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    gap: theme.spacing(2)
  },
  structureCard: {
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D",
    flexShrink: 0
  },
  propertyRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    color: "#1E1E1E",
    minWidth: 112
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    color: "#464646"
  },
  chip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: "11px",
    fontFamily: "Segoe UI",
    backgroundColor: "#EEF6F8",
    color: "#1E1E1E",
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  gridRoot: {
    "&.dx-widget .dx-datagrid .dx-datagrid-header-panel": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-toolbar": { backgroundColor: "#F5F7F7" },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td":
      {
        backgroundColor: "#E6E6E6",
        fontWeight: 600,
        fontSize: 12,
        color: "#1E1E1E",
        padding: 0,
        paddingLeft: theme.spacing(1),
        height: 24,
        borderRight: "solid 1px #aaa",
        borderLeft: "solid 1px #aaa"
      },
    "&.dx-widget .dx-datagrid .dx-row-lines > td": {
      border: "none",
      padding: "4px 8px",
      height: 40,
      color: "#464646",
      fontSize: 12,
      fontWeight: 400
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-rowsview .dx-row": {
      "&:hover": { backgroundColor: "#EEF6F8" }
    }
  },
  gridWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden"
  },
  rateCount: {
    fontSize: "12px",
    color: "#464646",
    fontFamily: "Segoe UI"
  }
}));

interface RateTableDetailsProps {
  agreementId: string;
  tableId: string;
}

interface RateRow {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic columns built from rate structure
  [key: string]: any;
}

export default function RateTableDetails({
  agreementId,
  tableId
}: RateTableDetailsProps) {
  const classes = useStyles();
  const { rateAgreement, loading, error } =
    useRateAgreementWithRates(agreementId);

  // Stable reference — only changes when the actual data changes.
  const rateTable = useMemo(
    () => rateAgreement?.rateTables.find(rt => rt.id === tableId),
    [rateAgreement, tableId]
  );

  // Build formatters once per currency — never recreated on unrelated re-renders.
  const [displayFormatter, fullFormatter] = useMemo(() => {
    const cur = rateTable?.currency as string | undefined;
    if (!cur) return [null, null];
    try {
      return [
        new Intl.NumberFormat("en", {
          style: "currency",
          currency: cur,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        new Intl.NumberFormat("en", {
          style: "currency",
          currency: cur,
          minimumFractionDigits: 0,
          maximumFractionDigits: 20
        })
      ];
    } catch {
      return [null, null];
    }
  }, [rateTable?.currency]);

  // Stable column definitions — new reference only when rateTable changes.
  const columns = useMemo(() => {
    if (!rateTable) return [];
    const { rateStructure } = rateTable;

    const pickupChildren = rateStructure.pickupLocationFilters.map((f, i) => ({
      colDef: {
        dataField: `pickup_${i}`,
        caption: filterColumnCaption(f.column, f.operator),
        allowSorting: true
      }
    }));
    const pickupCols =
      pickupChildren.length > 0
        ? [{ colDef: { caption: "Pickup" }, children: pickupChildren }]
        : [];

    const deliveryChildren = rateStructure.deliveryLocationFilters.map(
      (f, i) => ({
        colDef: {
          dataField: `delivery_${i}`,
          caption: filterColumnCaption(f.column, f.operator),
          allowSorting: true
        }
      })
    );
    const deliveryCols =
      deliveryChildren.length > 0
        ? [{ colDef: { caption: "Delivery" }, children: deliveryChildren }]
        : [];

    const serviceOptionChildren = rateStructure.serviceOptionFilters.map(
      (f, i) => ({
        colDef: {
          dataField: `option_${i}`,
          caption: filterColumnCaption(f.column, f.operator),
          allowSorting: true
        }
      })
    );
    const serviceOptionCols =
      serviceOptionChildren.length > 0
        ? [
            {
              colDef: { caption: "Service Options" },
              children: serviceOptionChildren
            }
          ]
        : [];

    const chargeCols = rateStructure.primaryQuantity.ranges.map((r, i) => {
      const caption =
        r.upperBound != null
          ? `${rateStructure.primaryQuantity.unit} ${filterOperatorSymbol(
              FilterOperator.LTE
            )} ${r.upperBound}`
          : `${rateStructure.primaryQuantity.unit} >`;
      // Size column to header text: ~7px per character + padding.
      // Ensures header is fully visible with no excess whitespace.
      const width = Math.max(70, caption.length * 7 + 24);
      return {
        colDef: {
          dataField: `charge_${i}`,
          caption,
          allowSorting: true,
          alignment: "right" as const,
          width
        },
        render: ({ data }: { data: RateRow }) => {
          const raw = data[`charge_${i}`] as number | undefined;
          if (raw == null) return null;
          const cur = rateTable.currency as string;
          const display = displayFormatter
            ? displayFormatter.format(raw)
            : `${cur} ${raw.toFixed(2)}`;
          const full = fullFormatter
            ? fullFormatter.format(raw)
            : `${cur} ${raw}`;
          return <span title={full}>{display}</span>;
        }
      };
    });

    return [
      ...pickupCols,
      ...deliveryCols,
      ...serviceOptionCols,
      ...chargeCols
    ];
  }, [rateTable, displayFormatter, fullFormatter]);

  // Stable row data — new reference only when rateTable changes.
  const rows = useMemo<RateRow[]>(() => {
    if (!rateTable) return [];
    return rateTable.rates.map(rate => {
      const row: RateRow = { id: rate.id };
      rate.pickupLocations.forEach((v, i) => {
        row[`pickup_${i}`] = v;
      });
      rate.deliveryLocations.forEach((v, i) => {
        row[`delivery_${i}`] = v;
      });
      rate.serviceOptions.forEach((v, i) => {
        row[`option_${i}`] = v;
      });
      rate.charges.forEach((v, i) => {
        row[`charge_${i}`] = v;
      });
      return row;
    });
  }, [rateTable]);

  // Wrap in CustomStore so DataGridView enables virtual scrolling
  // (remoteOperations = dataSource instanceof CustomStore).
  // Without this, DevExtreme renders all rows at once and overflows the
  // call stack with large datasets.
  const dataSource = useMemo(
    () =>
      new CustomStore({
        key: "id",
        loadMode: "raw",
        load: () => rows
      }),
    [rows]
  );

  // All hooks above — early returns after.
  if (error) return <ErrorBox apolloError={error} />;
  if (!loading && !rateTable) return null;

  const rateStructure = rateTable?.rateStructure ?? null;

  return (
    <div className={classes.root}>
      {loading && <LinearProgress />}
      <Card className={classes.structureCard}>
        <Section name="Rate Table Details">
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Type</Typography>
            <Typography className={classes.value} data-testid="rt-detail-type">
              {rateTable?.type}
            </Typography>
          </div>
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Currency</Typography>
            <Typography
              className={classes.value}
              data-testid="rt-detail-currency"
            >
              {rateTable?.currency}
            </Typography>
          </div>
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Description</Typography>
            <Typography
              className={classes.value}
              data-testid="rt-detail-description"
            >
              {rateTable?.description ?? "—"}
            </Typography>
          </div>
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Valid From</Typography>
            <Typography
              className={classes.value}
              component="div"
              data-testid="rt-detail-valid-from"
            >
              {rateTable && (
                <DateComponent
                  date={rateTable.validFrom}
                  variant={DateVariant.SHORT}
                />
              )}
            </Typography>
          </div>
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Valid To</Typography>
            <Typography
              className={classes.value}
              component="div"
              data-testid="rt-detail-valid-to"
            >
              {rateTable && (
                <DateComponent
                  date={rateTable.validTo}
                  variant={DateVariant.SHORT}
                />
              )}
            </Typography>
          </div>
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Min. Procedure</Typography>
            <Typography
              className={classes.value}
              data-testid="rt-detail-min-procedure"
            >
              {rateTable ? (rateTable.useMinimumProcedure ? "Yes" : "No") : "—"}
            </Typography>
          </div>
        </Section>
        <Section name="Rate Structure">
          <div>
            {rateStructure?.pickupLocationFilters.map((f, i) => (
              <span
                key={i}
                className={classes.chip}
                data-testid="rt-pickup-filter"
              >
                Pickup: {f.column} ({f.operator})
              </span>
            ))}
            {rateStructure?.deliveryLocationFilters.map((f, i) => (
              <span
                key={i}
                className={classes.chip}
                data-testid="rt-delivery-filter"
              >
                Delivery: {f.column} ({f.operator})
              </span>
            ))}
            {rateStructure?.serviceOptionFilters.map((f, i) => (
              <span
                key={i}
                className={classes.chip}
                data-testid="rt-option-filter"
              >
                Option: {f.column} ({f.operator})
              </span>
            ))}
            {rateStructure && (
              <span className={classes.chip} data-testid="rt-primary-qty">
                {rateStructure.primaryQuantity.column} (
                {rateStructure.primaryQuantity.unit})
              </span>
            )}
            {rateStructure?.additionalQuantities.map((q, i) => (
              <span
                key={i}
                className={classes.chip}
                data-testid="rt-additional-qty"
              >
                {q.column} ({q.unit})
              </span>
            ))}
          </div>
        </Section>
      </Card>

      <div data-testid="rates-grid" className={classes.gridWrapper}>
        <DataGridView<RateRow>
          dataSource={dataSource}
          isLoading={loading}
          showFilterRow={true}
          allowSelection={false}
          height="100%"
          gridClasses={{ root: classes.gridRoot }}
          columns={columns}
          withToolbar
          toolbarItemRender={() => (
            <Typography className={classes.rateCount}>
              {rows.length} {rows.length === 1 ? "rate" : "rates"}
            </Typography>
          )}
        />
      </div>
    </div>
  );
}
